import { defineDocument } from '../../schema';
export const blogPlayerAndRoomAccessKickedUpANotchDocument = defineDocument({
    id: 'blog:player-and-room-access-kicked-up-a-notch',
    kind: 'blog',
    slug: 'player-and-room-access-kicked-up-a-notch',
    route: '/blog/player-and-room-access-kicked-up-a-notch',
    seo: {
        title: 'Player and Room Access Kicked Up a Notch',
        description: 'New tools for streamers to enforce Twitch block lists, remove players, and end games early',
        imageAsset: 'blogKicked',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Airhead Blog',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'Player and Room Access Kicked Up a Notch',
            },
        ],
        description: [
            {
                type: 'text',
                text: 'New tools for streamers to enforce Twitch block lists, remove players, and end games early',
            },
        ],
        imageAsset: 'blogKicked',
        imageAlt: 'Avatar being kicked from a game room',
        showDescription: true,
        background: 'blurred-image',
    },
    meta: {
        author: 'Jay',
        publishedAt: new Date('2026-02-17T00:00:00.000Z'),
        tags: ['features'],
    },
    sections: [
        {
            id: 'article',
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Airhead Games was built with streamers at the center. From ',
                        },
                        {
                            type: 'link',
                            href: '/discover/features/twitch#going-live',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Going Live detection',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' to ',
                        },
                        {
                            type: 'link',
                            href: '/discover/features/moderator-tools#player-access',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Twitch-gated rooms',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ', we have been steadily expanding the tools you need to run a smooth, safe stream. Today we are announcing new features to give you additional control over your game rooms and who gets to play.',
                        },
                    ],
                },
            ],
            sections: [
                {
                    id: 'article__disallow-blocked-banned-twitch-users',
                    title: [
                        {
                            type: 'text',
                            text: 'Disallow Blocked & Banned Twitch Users',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'You can now turn on ',
                                },
                                {
                                    type: 'strong',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Disallow Blocked & Banned',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' in the ',
                                },
                                {
                                    type: 'link',
                                    href: '/library?section=moderation',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Settings',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' page. When enabled, anyone on your Twitch blocked and banned lists will automatically be prevented from joining your game rooms. Your Twitch moderation carries over to Airhead Games without any additional setup in our platform. Just toggle one switch and you are done.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'This feature requires you to be logged in with Twitch and have ',
                                },
                                {
                                    type: 'link',
                                    href: '/discover/features/moderator-tools#player-access',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Require Twitch Login',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: " enabled. It works by checking players' Twitch IDs against your blocked and banned lists when they try to join your games. It is one more way we are deepening our integration with Twitch and making community game nights easier to moderate.",
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'article__remove-players-from-your-game-room',
                    title: [
                        {
                            type: 'text',
                            text: 'Remove Players from Your Game Room',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Sometimes a player needs to go. Maybe they are trolling, maybe they are AFK, or maybe the vibe just is not right. Hosts can now kick individual players directly from ',
                                },
                                {
                                    type: 'link',
                                    href: '/discover/features/poppy#in-game-menu',
                                    children: [
                                        {
                                            type: 'text',
                                            text: "Poppy's Menu",
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: '. Select the player, confirm, and they are removed from the room. You stay in control of who plays in your games.',
                                },
                            ],
                        },
                        {
                            type: 'image',
                            imageAsset: 'featureKickFromRoom',
                            imageAlt: 'The updated Poppy menu with Remove Player and End Game options',
                            caption: [
                                {
                                    type: 'text',
                                    text: 'The updated Poppy menu with Remove Player and End Game options.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'The ability to remove a player is available in all game rooms; no special settings are required. Hosts can remove players at any time from the lobby or an in-progress game. After removing someone, in-progress games will automatically adapt to the new number of players.',
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'article__end-game-from-poppy-menu',
                    title: [
                        {
                            type: 'text',
                            text: 'End Game from Poppy Menu',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: "You no longer need to hunt for a way out. Hover on Poppy during any game, click the menu, and you will find the option to End Game. This will close the game room for all players. Whether you want to end a round early, switch games, or wrap up the stream, it is just a few clicks away in Poppy's menu.",
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'article__built-for-the-stream',
                    title: [
                        {
                            type: 'text',
                            text: 'Built for the Stream',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'These updates are part of our ongoing focus on streamer and moderation-focused features. We want Airhead Games to feel like an extension of your channel. Tools that fit your workflow, respect your community standards, and help you run great streams. More is coming. In the meantime, ',
                                },
                                {
                                    type: 'link',
                                    href: '/login',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'log in with Twitch',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ', fire up a room, and put the new tools to work.',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'article-actions',
            widgets: [
                {
                    type: 'relatedDocuments',
                    limit: 4,
                },
            ],
        },
    ],
});
//# sourceMappingURL=player-and-room-access-kicked-up-a-notch.js.map